<template>
  <div class="solutions-step">
    <StepperHeading :title="'Solutions Found'" :step="3">
      <template #help>
        <p>Based on your inputs, the following solutions were found. Review the options below and select one to see more details, or proceed to the LCA Analysis to evaluate environmental impacts.</p>
      </template>
    </StepperHeading>
    <div class="solutions-container" v-if="solutions && solutions.length > 0">
      <!-- Solution Cards Grid with Scrollable Container -->
      <div class="solutions-grid-wrapper">
        <h3 class="solutions-section-title">
          <i class="bi bi-list-check"></i> Found Solutions ({{ solutions.length }})
        </h3>
        <div class="solutions-grid">
          <div 
            v-for="(solution, index) in solutions" 
            :key="solution.solution_id || solution.id"
            class="solution-card"
            @click="selectSolution(solution)"
          >
            <div class="solution-card-header">
              <span class="solution-number">{{ index + 1 }}</span>
              <h3 class="solution-title">{{ getProcessName(solution) }}</h3>
            </div>
            <div class="solution-card-body">
              <div class="solution-category">
                <div class="category-pill">
                  <i class="bi bi-tag-fill"></i>
                  {{ getSolutionCategory(solution) }}
                </div>
                <div class="subcategory-pill" v-if="getSolutionSubCategory(solution)">
                  {{ getSolutionSubCategory(solution) }}
                </div>
              </div>
              
              <!-- Neues schlichteres Design für die Score-Anzeige -->
              <div class="solution-score" v-if="getSolutionScore(solution)">
                <div class="score-header">
                  <span class="score-label">Match Quality</span>
                  <span class="score-value" :class="getScoreClass(getSolutionScore(solution))">
                    {{ getSolutionScore(solution) }}
                  </span>
                </div>
                <div class="score-bar-container">
                  <div class="score-bar-background">
                    <div 
                      class="score-bar-fill" 
                      :class="getScoreClass(getSolutionScore(solution))"
                      :style="{ width: `${getSolutionScore(solution)}%` }"
                    ></div>
                  </div>
                </div>
              </div>
              
              <p class="solution-description">{{ getDescription(solution) }}</p>
            </div>
            <div class="solution-card-footer">
              <button class="view-details-btn">
                <i class="bi bi-info-circle"></i> View Details
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Enhanced LCA Info Banner (after solutions grid) -->
      <div class="lca-info-banner enhanced-visibility">
        <div class="info-icon">
          <i class="bi bi-lightbulb"></i>
        </div>
        <div class="info-content">
          <h4>Environmental Impact Analysis Available</h4>
          <p>You can proceed to Environmental Impact Analysis to understand the environmental impact of food surplus solutions. <strong>Note:</strong> Environmental Impact Analysis is currently available only for solutions in the "Reuse (human)" category. The specific solution for environmental analysis will be selected in the next steps.</p>
          <div class="banner-footer">
            <button class="lca-analysis-btn-inline" @click="goToLCAAnalysis">
              <i class="bi bi-bar-chart-line"></i>
              Analyze Environmental Impact
            </button>
            <div class="banner-hint">
              <i class="bi bi-arrow-up-circle"></i> Select a solution above to view details or proceed to Environmental Impact Analysis
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Empty state when no solutions found -->
    <div class="empty-solutions" v-else>
      <div class="empty-state-icon">
        <i class="bi bi-search"></i>
      </div>
      <h3>No Solutions Found</h3>
      <p>We couldn't find any matching solutions based on your input. Try adjusting your surplus details or company information to find more relevant solutions.</p>
      <button class="back-btn" @click="goBack">
        <i class="bi bi-arrow-left"></i> Go Back to Food Surplus
      </button>
    </div>
  </div>
</template>

<script>
import { useStepperStore } from '@/stores/stepper';
import { usePageContextStore } from '@/stores/pageContext';
import { useLCAStore } from '@/stores/lca';
import StepperHeading from './StepperHeading.vue';
import { useRouter } from 'vue-router';
import { onMounted, computed } from 'vue';
import axios from 'axios';

export default {
  name: 'Solutions',
  components: { StepperHeading },
  setup() {
    const stepperStore = useStepperStore();
    const pageContextStore = usePageContextStore();
    const lcaStore = useLCAStore();
    const router = useRouter();

    pageContextStore.setPageContext(
      'Solutions Found',
      'Based on the information you provided, the matched solutions are displayed below. Select one to see more details or proceed to Environmental Impact Analysis.',
      'Solutions are returned from the AI matching process.'
    );

    const solutions = computed(() => stepperStore.solutions);

    function selectSolution(solution) {
      stepperStore.setSelectedSolution(solution);
      router.push({ name: 'solution-details', params: { id: solution.solution_id || solution.id } });
    }

    function goToLCAAnalysis() {
      router.push({ name: 'LCAStepperView' });
    }
    
    function goBack() {
      router.push({ name: 'food-surplus' });
    }

    async function fetchSolutions() {
      try {
        const userInputs = {
          companyProfile: { ...stepperStore.companyProfile },
          wasteInfo: { ...stepperStore.wasteInfo },
        };
        
        // Parameter für Score und Reasoning explizit auf "true" setzen
        const includeVCG = true;
        const includeScore = true;
        const includeReasoning = true;
        
        console.log("Fetching solutions with scores and reasoning...");
        
        // Erweiterte Query-Parameter für die API-Anfrage
        const queryParams = new URLSearchParams({
          includeVCG: includeVCG,
          includeScore: includeScore,
          includeReasoning: includeReasoning
        }).toString();
        
        const url = `/solutions/match?${queryParams}`;
        console.log("Request URL:", url);
        
        const response = await axios.post(url, { userInputs });
        const data = response.data;
        
        // Debug-Ausgabe der vollständigen API-Antwort
        console.log("API Response:", JSON.stringify(data, null, 2));

        if (data.solutions) {
          stepperStore.setSolutions(data.solutions);
          lcaStore.setSolutions(data.solutions);
          
          // Detaillierte Analyse der zurückgegebenen Details
          if (data.details) {
            console.log("Raw details from API:", data.details);
            
            // Überprüfung der Scores- und Reasoning-Daten
            if (data.details.scores) {
              console.log("Scores in API response:", data.details.scores);
              console.log("Score keys:", Object.keys(data.details.scores));
            } else {
              console.log("No scores found in API response details");
            }
            
            if (data.details.reasoning) {
              console.log("Reasoning in API response:", data.details.reasoning);
              console.log("Reasoning keys:", Object.keys(data.details.reasoning));
            } else {
              console.log("No reasoning found in API response details");
            }
            
            // Setze solutionDetails mit den Daten aus der API-Antwort
            stepperStore.setSolutionDetails(data.details);
            
            // Überprüfen, ob die Details korrekt gesetzt wurden
            console.log("Solution details after setting in store:", stepperStore.solutionDetails);
          } else {
            console.log("No details received from API");
            stepperStore.setSolutionDetails({ scores: {}, reasoning: {} });
          }
        } else {
          console.log("No solutions found in API response");
          stepperStore.setSolutions([]);
          lcaStore.setSolutions([]);
          stepperStore.setSolutionDetails({ scores: {}, reasoning: {} });
        }
      } catch (error) {
        console.error('Error matching solutions:', error);
        stepperStore.setSolutions([]);
        lcaStore.setSolutions([]);
        stepperStore.setSolutionDetails({ scores: {}, reasoning: {} });
      }
    }

    onMounted(async () => {
      console.log("Solutions component mounted");
      // REMOVED automatic fetching - the Stepper component should handle all fetching
      // This prevents duplicate API calls and race conditions
      if (stepperStore.solutions.length) {
        // Falls bereits Lösungen im Store sind, direkt an LCA-Store übergeben
        lcaStore.setSolutions(stepperStore.solutions);
        console.log("Solutions already in store:", stepperStore.solutions);
        console.log("Solution details already in store:", stepperStore.solutionDetails);
      } else {
        console.log("No solutions in store - waiting for data from Stepper component");
        // Don't fetch here - let the Stepper component handle it
      }
    });

    // --- Helper: unify field names for FoodCycle vs. VCG solutions
    function getProcessName(sol) {
      // FoodCycle => "process_name", VCG => "processName"
      return sol.process_name ?? sol.processName ?? '—';
    }
    
    function getDescription(sol) {
      // Beide können "description" haben. Fallback auf '—'
      return sol.description ?? '—';
    }
    
    function getSolutionCategory(sol) {
      // FoodCycle => "solution_category", VCG => "solutionCategory"
      return sol.solution_category ?? sol.solutionCategory ?? '—';
    }
    
    function getSolutionSubCategory(sol) {
      // FoodCycle => "solution_subcategory", VCG => "solutionSubcategory"
      return sol.solution_subcategory ?? sol.solutionSubcategory ?? '—';
    }
    
    // Extrahiere die effektive Solution-ID basierend auf Lösung-Typ
    function getEffectiveSolutionId(sol) {
      // VCG.ai-Lösungen haben manchmal andere ID-Formate
      if (sol.solution_origin === 'vcg.ai') {
        return sol.id || sol.solution_id;
      }
      // FoodCycle-Lösungen
      return sol.solution_id || sol._id;
    }
    
    // Helper für Score-Anzeige
    function getSolutionScore(sol) {
      const solutionId = getEffectiveSolutionId(sol);
      console.log(`Getting score for solution ${sol.process_name || sol.processName}, ID: ${solutionId}`);
      return stepperStore.getSolutionScore(solutionId);
    }
    
    // Neue vereinfachte Helfer-Methoden für das schlichtere Score-Design
    function getScoreClass(score) {
      if (score >= 80) return 'score-high';
      if (score >= 50) return 'score-medium';
      return 'score-low';
    }
    
    return {
      solutions,
      selectSolution,
      goToLCAAnalysis,
      goBack,
      fetchSolutions,
      // Helpers
      getProcessName,
      getDescription,
      getSolutionCategory,
      getSolutionSubCategory,
      getSolutionScore,
      // Neue Score-Helpers
      getScoreClass
    };
  },
};
</script>

<style scoped>
.solutions-step {
  width: 100%;
  padding: 0 0 2rem;
}

.solutions-container {
  animation: fadeIn 0.5s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Added section title for solutions */
.solutions-section-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* New wrapper for scrollable solutions grid */
.solutions-grid-wrapper {
  margin-top: 1.5rem;
  background-color: #fff;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(135, 177, 64, 0.1);
}

/* Solutions Grid with scrollable behavior - increased height */
.solutions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  max-height: 650px; /* Increased height as requested */
  overflow-y: auto;
  padding-right: 0.5rem;
  /* Smooth scrolling */
  scroll-behavior: smooth;
}

/* Scrollbar styling for modern browsers */
.solutions-grid::-webkit-scrollbar {
  width: 8px;
}

.solutions-grid::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.solutions-grid::-webkit-scrollbar-thumb {
  background: #c0d0a0;
  border-radius: 4px;
}

.solutions-grid::-webkit-scrollbar-thumb:hover {
  background: #87b140;
}

.solution-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(135, 177, 64, 0.1);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.solution-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: rgba(135, 177, 64, 0.3);
}

.solution-card-header {
  background-color: #f2f5e7;
  padding: 1.25rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
}

.solution-number {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: #87b140;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.solution-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #333;
  margin: 0;
  padding-right: 2rem;
  line-height: 1.4;
}

.solution-card-body {
  padding: 1.25rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.solution-category {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.category-pill {
  background-color: rgba(135, 177, 64, 0.15);
  color: #558000;
  padding: 0.4rem 0.8rem;
  border-radius: 30px;
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.category-pill i {
  font-size: 0.8rem;
}

.subcategory-pill {
  background-color: #f0f0f0;
  color: #555;
  padding: 0.4rem 0.8rem;
  border-radius: 30px;
  font-size: 0.85rem;
}

/* Neues schlichteres Score Design */
.solution-score {
  margin-bottom: 1rem;
  padding: 0.75rem;
  border-radius: 8px;
  background-color: #f8f9fa;
  border: 1px solid #eee;
}

.score-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.score-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #555;
}

.score-value {
  font-size: 1rem;
  font-weight: 700;
}

.score-value.score-high {
  color: #2e7d32;
}

.score-value.score-medium {
  color: #f57f17;
}

.score-value.score-low {
  color: #c62828;
}

.score-bar-container {
  width: 100%;
}

.score-bar-background {
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
}

.score-bar-fill {
  height: 100%;
  transition: width 0.5s ease;
  border-radius: 3px;
}

.score-bar-fill.score-high {
  background-color: #4caf50;
}

.score-bar-fill.score-medium {
  background-color: #ffc107;
}

.score-bar-fill.score-low {
  background-color: #f44336;
}

.solution-description {
  color: #666;
  line-height: 1.6;
  margin-top: 0.5rem;
  font-size: 0.95rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.solution-card-footer {
  padding: 0 1.25rem 1.25rem;
}

.view-details-btn {
  background-color: transparent;
  color: #87b140;
  border: 1px solid #87b140;
  border-radius: 6px;
  padding: 0.6rem 1rem;
  width: 100%;
  font-weight: 600;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.view-details-btn:hover {
  background-color: rgba(135, 177, 64, 0.1);
}

/* Enhanced LCA Info Banner (moved back to bottom but with enhanced visibility) */
.lca-info-banner {
  background-color: #f2f5e7;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0 1.5rem 0;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #87b140;
}

/* Enhanced visibility for LCA banner */
.lca-info-banner.enhanced-visibility {
  background-color: #f0f7e6;
  border: 1px solid rgba(135, 177, 64, 0.3);
  box-shadow: 0 4px 15px rgba(135, 177, 64, 0.1);
  transition: all 0.3s ease;
}

.lca-info-banner.enhanced-visibility:hover {
  box-shadow: 0 6px 20px rgba(135, 177, 64, 0.15);
  transform: translateY(-2px);
}

.info-icon {
  background-color: rgba(135, 177, 64, 0.2);
  color: #87b140;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.info-content {
  flex-grow: 1;
}

.info-content h4 {
  margin: 0 0 0.5rem;
  color: #333;
  font-weight: 700;
  font-size: 1.1rem;
}

.info-content p {
  margin: 0 0 1rem;
  color: #555;
  line-height: 1.6;
  font-size: 0.95rem;
}

/* Banner footer with button and hint */
.banner-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

/* Banner hint */
.banner-hint {
  color: #666;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Inline LCA button within the banner */
.lca-analysis-btn-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #87b140;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.6rem 1.2rem;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(135, 177, 64, 0.2);
}

.lca-analysis-btn-inline:hover {
  background-color: #75a236;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(135, 177, 64, 0.3);
}

/* Empty state styling */
.empty-solutions {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 2rem;
  margin-top: 1.5rem;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.empty-state-icon {
  background-color: #f2f5e7;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #87b140;
  margin-bottom: 1.5rem;
}

.empty-solutions h3 {
  color: #333;
  font-weight: 700;
  margin-bottom: 1rem;
}

.empty-solutions p {
  color: #666;
  max-width: 500px;
  margin-bottom: 1.5rem;
  line-height: 1.6;
}

.back-btn {
  background-color: #f0f0f0;
  color: #444;
  border: none;
  border-radius: 8px;
  padding: 0.8rem 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background-color: #e5e5e5;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .solutions-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
    max-height: 550px;
  }

  .solution-card-header,
  .solution-card-body,
  .solution-card-footer {
    padding: 1rem;
  }

  .lca-info-banner {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 1.25rem;
  }

  .info-icon {
    margin-bottom: 0.5rem;
  }
  
  .banner-footer {
    width: 100%;
    align-items: center;
  }
  
  .lca-analysis-btn-inline {
    width: 100%;
    justify-content: center;
  }
  
  .banner-hint {
    text-align: center;
  }
}

@media (max-width: 576px) {
  .solutions-grid {
    grid-template-columns: 1fr;
    max-height: 500px;
  }

  .solution-title {
    font-size: 1.1rem;
  }

  .category-pill,
  .subcategory-pill {
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;
  }

  .solution-description {
    font-size: 0.9rem;
  }

  .lca-analysis-btn-inline {
    width: 100%;
    justify-content: center;
    font-size: 0.9rem;
  }
  
  .banner-hint {
    font-size: 0.85rem;
  }
}
</style>